// Angular
import { ChangeDetectionStrategy, Component, forwardRef, Injector, ViewChild } from "@angular/core";
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from "@angular/forms";
// BCDFX - Globals
import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

// Services
// Enums - ATDL
import { ATDLNParameterType } from "../../data-atdl/enums/atdl-parameter-type.enum";
// Enums
import { NumberSpinnerComponent, ZERO } from "shared/base-components/number-spinner.component";
import { ICDBaseSpinnerComponent } from "./icd-base-spinner.component";
import { ICD_BASE_DEFAULT_HOST_RECORD } from "./icd-base.component";

export const DYN_PERCENT_SPINNER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => ICDPercentSpinnerComponent),
  multi: true,
};

@Component({
  selector: "icd-percent-spinner",
  host: {
    ...ICD_BASE_DEFAULT_HOST_RECORD,
    "[class.icd__base__spinner__container]": "true",
  },
  templateUrl: "./html/icd-number-spinner.html",
  providers: [DYN_PERCENT_SPINNER_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ICDPercentSpinnerComponent
  extends ICDBaseSpinnerComponent
  implements ControlValueAccessor
{
  /************************************/
  @ViewChild("number_spinner", { static: true }) private numSpinner: NumberSpinnerComponent;

  constructor(injector: Injector) {
    super(injector);
  }

  protected override initConfigMaxValue() {
    this.cd.markForCheck();

    if (
      this.model.validity.hasOwnProperty("maxValue") &&
      typeof this.model.validity.maxValue === "number"
    ) {
      if (
        (this.config.allowNegativeNum && this.model.validity.maxValue < 0) ||
        this.model.validity.maxValue >= 0
      ) {
        // Technically we can have percentages > 100 but ....
        let maxValue = new CubiqTS.BigNumber(this.model.validity.maxValue);
        if (maxValue.isGreaterThan(100)) {
          maxValue = new CubiqTS.BigNumber(100);
        }
        this.config.maxValue = maxValue;
        this.config.maxValueSet = true;
        // adjust empty value
        if (this.config.maxValue.isLessThan(0)) {
          this.config.emptyValue = this.config.maxValue;
        }
      }
    } else {
      this.config.maxValue = null;
      this.config.maxValueSet = false;
    }
  }

  protected override initConfigMinValue() {
    this.cd.markForCheck();

    if (
      this.model.validity.hasOwnProperty("minValue") &&
      typeof this.model.validity.minValue === "number"
    ) {
      if (!this.config.allowNegativeNum && this.model.validity.minValue <= 0) {
        this.config.minValue = ZERO;
        this.config.minValueSet = true;
      } else {
        let minValue = new CubiqTS.BigNumber(this.model.validity.minValue);

        if (
          minValue.isGreaterThan(0) &&
          minValue.isLessThan(100) &&
          !this.config.maxValue.isLessThan(minValue)
        ) {
          this.config.minValue = minValue;
        } else {
          this.config.minValue = ZERO;
        }
        // adjust empty value
        if (this.config.minValue.isGreaterThan(0)) {
          this.config.emptyValue = this.config.minValue;
        }
      }
    } else if (!this.config.allowNegativeNum) {
      this.config.minValueSet = true;
      this.config.minValue = ZERO;
    } else {
      this.config.minValueSet = false;
      this.config.minValue = null;
    }
  }

  /**********************************************************************/
  /* DYNAMIC CONTROL - HANDLERS */

  protected override handleConstraintUpdate(data: any): void {
    if (
      data instanceof Object &&
      data.hasOwnProperty("value") &&
      data.hasOwnProperty("name") &&
      typeof data.name === "string"
    ) {
      // STATE FLOW RULES
      this.handleDynResponses(this.processExternalStateFlowDependency(data, new Map()));
    } else {
      logger.out.error(
        `ERROR: ${this.constructor.name}.handleConstraintUpdate(): ICD [${this.icdId}]: Invalid constraint data! Data dump: %O`,
        data,
      );
    }
  }

  protected override getDefaultLabel(): string {
    return "Percent Spinner";
  }
  protected override getAllowedTypes(): ATDLNParameterType[] {
    return [ATDLNParameterType.PERCENT];
  }

  protected override get numberSpinner(): NumberSpinnerComponent {
    return this.numSpinner;
  }

  protected override initialiseConfiguration(): void {
    this.cd.markForCheck();
    this.config.precision = 2;
    this.config.minValue = ZERO;
    this.config.maxValue = new CubiqTS.BigNumber(100);
    this.config.emptyValue = ZERO;
    this.config.minValueSet = true;
    this.config.maxValueSet = true;
    this.config.allowNegativeNum = false;
  }
}
